import colors from './colors';
import { CALENDAR_START_HOURS } from './calendar/calendarConstants';
import { rawColors } from './theme/rawColors';

export const WEEK_TIME_COLORS = {
  sleep: '#4C9BE8',
  classTime: '#E57373',
  commute: '#A67C52',
  activities: '#4ECDC4',
  events: '#8492A5',
  usedStudy: colors.aqua,
  // Planned study time's purple, striped (`striped` on its items): planned, but it didn't happen.
  missedStudy: rawColors.purple,
  plannedStudy: rawColors.purple,
  freeStudy: '#AABDFE'
};

/**
 * Diagonal stripes of a colour, for a CSS background: the colour, then the same at 35%. Missed sessions wear it in the
 * week donut's legend and Auto-plan's "won't fit" does in red, so a stripe always reads "didn't, or won't, happen".
 */
export const stripesOf = (color: string) => `repeating-linear-gradient(135deg, ${color} 0 2px, ${color}59 2px 4px)`;

export type WeekTimeGroupKey =
  | 'sleep'
  | 'classTime'
  | 'commute'
  | 'activities'
  | 'events'
  | 'usedStudy'
  | 'missedStudy'
  | 'plannedStudy'
  | 'freeStudy';

export type DateLike = Date | string | number | { toDate?: () => Date };

export type WeekTimeEvent = {
  start: DateLike;
  end: DateLike;
  type?: string;
  busy?: boolean;
  allDay?: boolean | string;
  commuteBefore?: number | null;
  commuteAfter?: number | null;
  courseId?: number;
  title?: string;
  colorHex?: string;
  isUsed?: boolean;
  /** The session's task is complete. */
  isTaskCompleted?: boolean;
  /** What a planned block knows about its course: the name, and the colour as its text colour. */
  courseName?: string;
  textColor?: string;
  task?: { courseId?: number; courseName?: string; colorHex?: string };
};

export type WeekTimeCatalogItem = {
  id?: number;
  name: string;
  colorHex?: string;
};

export type WeekTimeItem = {
  key: string;
  label: string;
  minutes: number;
  color: string;
  /** Drawn in stripes of its colour: missed sessions. */
  striped?: boolean;
};

export type WeekTimeGroup = {
  key: WeekTimeGroupKey;
  label: string;
  minutes: number;
  color: string;
  striped?: boolean;
  children?: WeekTimeItem[];
};

export type WeekTimeBudget = {
  totalMinutes: number;
  freeStudyMinutes: number;
  isWeek: boolean;
  groups: WeekTimeGroup[];
  slices: WeekTimeItem[];
};

const CLASS_TYPES = new Set(['CLASS', 'CANVAS', 'BRIGHTSPACE', 'MOODLE']);
const ACTIVITY_TYPES = new Set(['ACTIVITY', 'MEAL', 'ME_TIME']);
const EVENT_TYPES = new Set(['EVENT', 'GOOGLE_EVENT']);
const SKIP_TYPES = new Set([
  'STUDY_TIME',
  'EXTRA_TIME',
  'AWAKE_TIME',
  'SLEEP_TIME',
  'TASK',
  'HOLIDAY',
  'COMMUTE',
  'COMMUTE_BEFORE',
  'COMMUTE_AFTER'
]);

const MS_PER_MINUTE = 60 * 1000;
const MINUTES_PER_DAY = 24 * 60;
const WEEK_LENGTH_DAYS = 7;

/** Planned blocks with no task behind them, so no course to name. */
const NO_COURSE_LABEL = 'No course';

const GROUP_LABELS: Record<WeekTimeGroupKey, string> = {
  sleep: 'Sleep',
  classTime: 'Class time',
  commute: 'Commute',
  activities: 'Activities',
  events: 'Events',
  usedStudy: 'Logged study time',
  missedStudy: 'Missed sessions',
  plannedStudy: 'Planned study time',
  freeStudy: 'Free study time'
};

export const toJsDate = (value?: DateLike | null): Date | null => {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      const date = value.toDate();
      if (date instanceof Date && !Number.isNaN(date.getTime())) return date;
    }
    const valueOf = (value as { valueOf?: () => unknown }).valueOf;
    if (typeof valueOf === 'function') {
      const raw = valueOf.call(value);
      if (typeof raw === 'number' && !Number.isNaN(raw)) return new Date(raw);
    }
  }
  return null;
};

const toTimestamp = (value?: DateLike | null): number | null => {
  const date = toJsDate(value);
  return date ? date.getTime() : null;
};

export const overlappingMinutes = (start: DateLike, end: DateLike, rangeStart: DateLike, rangeEnd: DateLike): number => {
  const startMs = toTimestamp(start);
  const endMs = toTimestamp(end);
  const rangeStartMs = toTimestamp(rangeStart);
  const rangeEndMs = toTimestamp(rangeEnd);
  if (startMs == null || endMs == null || rangeStartMs == null || rangeEndMs == null) return 0;
  const overlapStart = Math.max(startMs, rangeStartMs);
  const overlapEnd = Math.min(endMs, rangeEndMs);
  return Math.max(0, (overlapEnd - overlapStart) / MS_PER_MINUTE);
};

/** FullCalendar dates are midnight; a column actually runs 04:00–04:00 (`slotMinTime` / `slotMaxTime` 28:00). Snap the clock, don't add 4h, or a 1-day `activeEnd` already at 04:00 becomes 08:00. */
export const shiftRangeToCalendarDays = (rangeStart: DateLike, rangeEnd: DateLike) => {
  const from = toJsDate(rangeStart);
  const to = toJsDate(rangeEnd);
  if (!from || !to) return null;
  const atDayStart = (value: Date) => {
    const next = new Date(value.getTime());
    next.setHours(CALENDAR_START_HOURS, 0, 0, 0);
    return next;
  };
  return {
    from: atDayStart(from),
    to: atDayStart(to)
  };
};

const isAllDay = (event: WeekTimeEvent) => event.allDay === true || event.allDay === 'true';

const addClippedMinutes = (event: WeekTimeEvent, rangeStart: Date, rangeEnd: Date) => {
  const start = toJsDate(event.start);
  const end = toJsDate(event.end);
  if (!start || !end) return 0;
  return overlappingMinutes(start, end, rangeStart, rangeEnd);
};

export const leftoverTone = (freeStudyMinutes: number) => {
  if (freeStudyMinutes < 0) return 'negative' as const;
  if (freeStudyMinutes < 10 * 60) return 'warning' as const;
  return 'positive' as const;
};

export const formatCompactHours = (minutes: number) => `${Math.round(minutes / 60)}h`;

export const formatHoursOneDecimal = (minutes: number) => `${(Math.max(0, minutes) / 60).toFixed(1)}h`;

export const slicePercent = (minutes: number, totalMinutes: number) =>
  totalMinutes <= 0 ? 0 : Math.round((Math.max(0, minutes) / totalMinutes) * 100);

/** Integers that sum to `target`, largest remainder first. */
const roundToSum = (parts: number[], target: number): number[] => {
  if (parts.length === 0) return [];
  if (target <= 0) return parts.map(() => 0);
  const floored = parts.map(part => Math.floor(part));
  let remaining = target - floored.reduce((sum, part) => sum + part, 0);
  const order = parts
    .map((part, index) => ({ index, frac: part - Math.floor(part) }))
    .sort((a, b) => b.frac - a.frac || parts[b.index] - parts[a.index] || a.index - b.index);
  const result = [...floored];
  for (let i = 0; remaining > 0 && i < order.length; i += 1) {
    result[order[i].index] += 1;
    remaining -= 1;
  }
  for (let i = order.length - 1; remaining < 0 && i >= 0; i -= 1) {
    if (result[order[i].index] > 0) {
      result[order[i].index] -= 1;
      remaining += 1;
    }
  }
  return result;
};

/**
 * Percents of the visible range. Rows that fit in the range are rounded together so they
 * sum to 100 (independent `Math.round` can land on 99 or 101). Over-allocated weeks keep
 * each row as a share of the range, so the numbers can go past 100.
 */
export const allocatePercents = (minutes: number[], totalMinutes: number): number[] => {
  if (minutes.length === 0) return [];
  if (totalMinutes <= 0) return minutes.map(() => 0);
  const weights = minutes.map(value => Math.max(0, value));
  const sum = weights.reduce((total, value) => total + value, 0);
  if (sum === 0) return minutes.map(() => 0);
  if (sum > totalMinutes + 1e-6) {
    return weights.map(value => slicePercent(value, totalMinutes));
  }
  return roundToSum(
    weights.map(value => (value / totalMinutes) * 100),
    100
  );
};

/** Child percents of the week, rounded so they add back to the parent row. */
export const allocateChildPercents = (childMinutes: number[], parentMinutes: number, parentPercent: number): number[] => {
  if (childMinutes.length === 0) return [];
  if (parentMinutes <= 0) return childMinutes.map(() => 0);
  return roundToSum(
    childMinutes.map(value => (Math.max(0, value) / parentMinutes) * parentPercent),
    parentPercent
  );
};

const toCssColor = (hex: string | undefined, fallback: string) => {
  if (!hex) return fallback;
  return hex.startsWith('#') ? hex : `#${hex}`;
};

const addNamedMinutes = (buckets: Map<string, WeekTimeItem>, key: string, minutes: number, label: string, color: string) => {
  const existing = buckets.get(key);
  if (existing) {
    existing.minutes += minutes;
    return;
  }
  buckets.set(key, { key, label, minutes, color });
};

const resolveNamedItem = (
  event: WeekTimeEvent,
  catalog: WeekTimeCatalogItem[],
  fallbackLabel: string,
  fallbackColor: string,
  keyPrefix: string
): Omit<WeekTimeItem, 'minutes'> => {
  const match = event.courseId != null ? catalog.find(item => item.id === event.courseId) : undefined;
  const label = match?.name || event.title || fallbackLabel;
  const key = match?.id != null ? `${keyPrefix}:${match.id}` : `${keyPrefix}:title:${label}`;
  return {
    key,
    label,
    color: toCssColor(match?.colorHex || event.colorHex, fallbackColor)
  };
};

/**
 * A study block grouped by its course. These blocks carry the course by name rather than by id,
 * so the catalog is matched on that too; blocks without one are collected together.
 *
 * `group` keeps used and planned in separate buckets and gives each its own fallback colour, so a
 * course with no colour of its own still reads as the group it belongs to.
 */
const resolveStudyItem = (
  event: WeekTimeEvent,
  courses: WeekTimeCatalogItem[],
  group: 'used' | 'missed' | 'planned'
): Omit<WeekTimeItem, 'minutes'> => {
  const courseId = event.task?.courseId ?? event.courseId;
  const courseName = event.courseName || event.task?.courseName;
  const byId = courseId != null ? courses.find(course => course.id === courseId) : undefined;
  const named = byId || (courseName ? courses.find(course => course.name === courseName) : undefined);
  const label = named?.name || courseName || NO_COURSE_LABEL;
  const fallback =
    group === 'used'
      ? WEEK_TIME_COLORS.usedStudy
      : group === 'missed'
      ? WEEK_TIME_COLORS.missedStudy
      : WEEK_TIME_COLORS.plannedStudy;
  return {
    key: named?.id != null ? `${group}:${named.id}` : `${group}:${label}`,
    label,
    color: toCssColor(named?.colorHex || event.task?.colorHex || event.textColor, fallback)
  };
};

const toSortedItems = (buckets: Map<string, WeekTimeItem>) =>
  Array.from(buckets.values())
    .filter(item => item.minutes > 0)
    .sort((a, b) => b.minutes - a.minutes);

const minutesOf = (items: WeekTimeItem[]) => items.reduce((sum, item) => sum + item.minutes, 0);

/**
 * `now` splits missed sessions out of planned study time: a session that ended without being marked used, the way the
 * Auto-plan dialog counts one. An ended session of a completed task counts as used instead: the calendar already
 * crosses it out as done, and "missed" is time still to make up (Petr). Without `now` every unused session counts as
 * planned.
 */
export const buildWeekTimeBudget = (
  events: WeekTimeEvent[],
  rangeStart: DateLike,
  rangeEnd: DateLike,
  catalog: { courses?: WeekTimeCatalogItem[]; activities?: WeekTimeCatalogItem[] } = {},
  now?: DateLike
): WeekTimeBudget | null => {
  const from = toJsDate(rangeStart);
  const to = toJsDate(rangeEnd);
  if (!from || !to) return null;
  const nowAt = toJsDate(now);

  const rangeMinutes = overlappingMinutes(from, to, from, to);
  if (rangeMinutes <= 0) return null;

  const courses = catalog.courses || [];
  const activities = catalog.activities || [];
  const classBuckets = new Map<string, WeekTimeItem>();
  const activityBuckets = new Map<string, WeekTimeItem>();
  const usedBuckets = new Map<string, WeekTimeItem>();
  const missedBuckets = new Map<string, WeekTimeItem>();
  const plannedBuckets = new Map<string, WeekTimeItem>();

  let awake = 0;
  let commute = 0;
  let eventsMinutes = 0;
  let usedStudy = 0;
  let missedStudy = 0;
  let plannedStudy = 0;

  events.forEach(event => {
    const type = event.type || '';
    if (type === 'AWAKE_TIME') {
      awake += addClippedMinutes(event, from, to);
      return;
    }

    if (isAllDay(event) || SKIP_TYPES.has(type)) return;

    const minutes = addClippedMinutes(event, from, to);

    if (type === 'PLANNED_TASK' || type === 'PLANNED_STUDY_TIME') {
      const end = toJsDate(event.end);
      const ended = !!nowAt && !!end && end.getTime() <= nowAt.getTime();
      const used = !!event.isUsed || (ended && !!event.isTaskCompleted);
      const missed = ended && !used;
      const item = resolveStudyItem(event, courses, used ? 'used' : missed ? 'missed' : 'planned');
      if (used) {
        usedStudy += minutes;
        addNamedMinutes(usedBuckets, item.key, minutes, item.label, item.color);
      } else if (missed) {
        missedStudy += minutes;
        addNamedMinutes(missedBuckets, item.key, minutes, item.label, item.color);
      } else {
        plannedStudy += minutes;
        addNamedMinutes(plannedBuckets, item.key, minutes, item.label, item.color);
      }
      return;
    }

    if (CLASS_TYPES.has(type)) {
      const item = resolveNamedItem(event, courses, GROUP_LABELS.classTime, WEEK_TIME_COLORS.classTime, 'class');
      addNamedMinutes(classBuckets, item.key, minutes, item.label, item.color);
    } else if (ACTIVITY_TYPES.has(type)) {
      const item = resolveNamedItem(event, activities, GROUP_LABELS.activities, WEEK_TIME_COLORS.activities, 'activity');
      addNamedMinutes(activityBuckets, item.key, minutes, item.label, item.color);
    } else if (EVENT_TYPES.has(type)) {
      if (type === 'GOOGLE_EVENT' && event.busy === false) return;
      eventsMinutes += minutes;
    }

    const start = toJsDate(event.start);
    const end = toJsDate(event.end);
    if (!start || !end) return;
    if (event.commuteBefore) {
      commute += overlappingMinutes(new Date(start.getTime() - event.commuteBefore * MS_PER_MINUTE), start, from, to);
    }
    if (event.commuteAfter) {
      commute += overlappingMinutes(end, new Date(end.getTime() + event.commuteAfter * MS_PER_MINUTE), from, to);
    }
  });

  if (awake <= 0) return null;

  const classItems = toSortedItems(classBuckets);
  const activityItems = toSortedItems(activityBuckets);
  const usedItems = toSortedItems(usedBuckets);
  // Each course's missed time in that course's colour, striped like the group.
  const missedItems = toSortedItems(missedBuckets).map(item => ({ ...item, striped: true }));
  const plannedItems = toSortedItems(plannedBuckets);
  const classTime = minutesOf(classItems);
  const activityTime = minutesOf(activityItems);
  const sleep = Math.max(0, rangeMinutes - awake);
  const leftoverMinutes =
    awake - classTime - commute - activityTime - eventsMinutes - usedStudy - missedStudy - plannedStudy;

  const groups: WeekTimeGroup[] = [
    { key: 'sleep', label: GROUP_LABELS.sleep, minutes: sleep, color: WEEK_TIME_COLORS.sleep },
    {
      key: 'classTime',
      label: GROUP_LABELS.classTime,
      minutes: classTime,
      color: WEEK_TIME_COLORS.classTime,
      children: classItems
    },
    { key: 'commute', label: GROUP_LABELS.commute, minutes: commute, color: WEEK_TIME_COLORS.commute },
    {
      key: 'activities',
      label: GROUP_LABELS.activities,
      minutes: activityTime,
      color: WEEK_TIME_COLORS.activities,
      children: activityItems
    },
    { key: 'events', label: GROUP_LABELS.events, minutes: eventsMinutes, color: WEEK_TIME_COLORS.events },
    {
      key: 'usedStudy',
      label: GROUP_LABELS.usedStudy,
      minutes: usedStudy,
      color: WEEK_TIME_COLORS.usedStudy,
      children: usedItems
    },
    {
      key: 'missedStudy',
      label: GROUP_LABELS.missedStudy,
      minutes: missedStudy,
      color: WEEK_TIME_COLORS.missedStudy,
      striped: true,
      children: missedItems
    },
    {
      key: 'plannedStudy',
      label: GROUP_LABELS.plannedStudy,
      minutes: plannedStudy,
      color: WEEK_TIME_COLORS.plannedStudy,
      children: plannedItems
    },
    {
      key: 'freeStudy',
      label: GROUP_LABELS.freeStudy,
      minutes: Math.max(0, leftoverMinutes),
      color: WEEK_TIME_COLORS.freeStudy
    }
  ];

  const slices: WeekTimeItem[] = [
    { key: 'sleep', label: GROUP_LABELS.sleep, minutes: sleep, color: WEEK_TIME_COLORS.sleep },
    ...classItems,
    { key: 'commute', label: GROUP_LABELS.commute, minutes: commute, color: WEEK_TIME_COLORS.commute },
    ...activityItems,
    { key: 'events', label: GROUP_LABELS.events, minutes: eventsMinutes, color: WEEK_TIME_COLORS.events },
    { key: 'usedStudy', label: GROUP_LABELS.usedStudy, minutes: usedStudy, color: WEEK_TIME_COLORS.usedStudy },
    {
      key: 'missedStudy',
      label: GROUP_LABELS.missedStudy,
      minutes: missedStudy,
      color: WEEK_TIME_COLORS.missedStudy,
      striped: true
    },
    { key: 'plannedStudy', label: GROUP_LABELS.plannedStudy, minutes: plannedStudy, color: WEEK_TIME_COLORS.plannedStudy },
    {
      key: 'freeStudy',
      label: GROUP_LABELS.freeStudy,
      minutes: Math.max(0, leftoverMinutes),
      color: WEEK_TIME_COLORS.freeStudy
    }
  ].filter(item => item.minutes > 0);

  const dayCount = rangeMinutes / MINUTES_PER_DAY;

  return {
    totalMinutes: rangeMinutes,
    freeStudyMinutes: leftoverMinutes,
    isWeek: dayCount >= WEEK_LENGTH_DAYS - 0.5 && dayCount <= WEEK_LENGTH_DAYS + 0.5,
    groups,
    slices
  };
};
